import type { Workbook, Row, Worksheet } from 'exceljs';
import { yearLabel } from './format';
import type { CategoryGroup, DerivedBudget } from './totals';
import type { ProjectedLine } from './projection';

// Formatted .xlsx export. Mirrors the section structure of the CSV
// builder but with proper styling — bold headers, fills on totals,
// number formatting, frozen header row, page setup.
//
// CCAANZ palette (mirrors design_reference.md). ARGB format for ExcelJS:
//   FF prefix = full opacity
const BRAND = 'FF85754D'; // CCAANZ gold
const BRAND_LIGHT = 'FFF5F0E8'; // Brand-light fill
const BLUE = 'FF005487'; // CCAANZ blue
const BLUE_LIGHT = 'FFE5EEF4'; // Blue-light fill
const TEXT = 'FF111827'; // Primary text
const TEXT_SOFT = 'FF6B7280'; // Secondary text
const BORDER = 'FFE5E0D5'; // Warm tan border

// Excel custom number format. Format: positive ; negative ; zero ; text.
//   1,234 / (5,678) / -  /
const ACCOUNTING = '#,##0;(#,##0);"-";@';

const round = (n: number) => Math.round(n);

type Kind =
  | 'header' // Top headers row
  | 'section-header' // OPERATING — INCOME, OPERATING — EXPENSES, etc.
  | 'category' // Grants & Donations, Office of the Bishop, etc.
  | 'data'
  | 'subtotal'
  | 'total' // TOTAL OPERATING INCOME, TOTAL OPERATING EXPENSES, etc.
  | 'net' // Net Operating, Net — Project, TOTAL DISCRETE NET
  | 'grand' // GRAND TOTAL NET
  | 'project-header' // Project: Risen Camp
  | 'project-subhead'; // Income / Expenses subheads

// Apply styling per row kind. Keeps the row-emitting code readable.
function styleRow(row: Row, kind: Kind): void {
  switch (kind) {
    case 'header':
      row.font = { bold: true, color: { argb: BRAND }, size: 11 };
      row.alignment = { vertical: 'middle', wrapText: false };
      row.height = 24;
      row.eachCell((cell) => {
        cell.border = { bottom: { style: 'medium', color: { argb: BORDER } } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFFFF' },
        };
      });
      break;

    case 'section-header':
      row.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      row.height = 22;
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: BLUE },
        };
      });
      break;

    case 'category':
      row.font = { bold: true, color: { argb: BLUE }, size: 11 };
      row.height = 18;
      break;

    case 'data':
      row.font = { color: { argb: TEXT }, size: 10 };
      row.alignment = { vertical: 'middle' };
      break;

    case 'subtotal':
      row.font = { italic: true, color: { argb: TEXT_SOFT }, size: 10 };
      row.eachCell((cell) => {
        cell.border = { top: { style: 'thin', color: { argb: BORDER } } };
      });
      break;

    case 'total':
      row.font = { bold: true, color: { argb: TEXT }, size: 11 };
      row.height = 22;
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'medium', color: { argb: TEXT } },
          bottom: { style: 'thin', color: { argb: TEXT } },
        };
      });
      break;

    case 'net':
      row.font = { bold: true, color: { argb: BLUE }, size: 12 };
      row.height = 24;
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: BLUE_LIGHT },
        };
        cell.border = {
          top: { style: 'medium', color: { argb: BLUE } },
          bottom: { style: 'medium', color: { argb: BLUE } },
        };
      });
      break;

    case 'grand':
      row.font = { bold: true, color: { argb: BLUE }, size: 14 };
      row.height = 28;
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: BLUE_LIGHT },
        };
        cell.border = {
          top: { style: 'medium', color: { argb: BLUE } },
          bottom: { style: 'medium', color: { argb: BLUE } },
        };
      });
      break;

    case 'project-header':
      row.font = { bold: true, color: { argb: BRAND }, size: 12 };
      row.height = 20;
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: BRAND_LIGHT },
        };
      });
      break;

    case 'project-subhead':
      row.font = { bold: true, color: { argb: TEXT_SOFT }, size: 9 };
      row.alignment = { vertical: 'middle' };
      break;
  }
}

/** Add a fully-styled "Budget Projection" worksheet to an existing
 *  workbook. Used both by the standalone budget xlsx export and by
 *  the combined budget+cashflow workbook. */
export function addBudgetWorksheet(
  wb: Workbook,
  derived: DerivedBudget,
  baseYear: number,
  sheetName: string = 'Budget Projection',
): Worksheet {
  const { defaults } = derived;
  const ws = wb.addWorksheet(sheetName, {
    properties: { defaultRowHeight: 16 },
    views: [{ state: 'frozen', ySplit: 1 }],
    pageSetup: {
      orientation: 'landscape',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.4, right: 0.4, top: 0.4, bottom: 0.4, header: 0.3, footer: 0.3 },
      printTitlesRow: '1:1',
    },
    headerFooter: {
      oddHeader:
        '&L&"Montserrat,Bold"&12CCAANZ Diocesan Budget Tool&R&"Montserrat,Regular"&10Three-Year Projection',
      oddFooter:
        '&L&"Montserrat,Regular"&9Office of the Registrar&C&"Montserrat,Regular"&9Page &P of &N&R&"Montserrat,Regular"&9&D',
    },
  });

  ws.columns = [
    { header: '#', key: 'lineNo', width: 6 },
    // Single description column carries section headers, category names,
    // account names (indented), subtotal labels, totals, and Net rows.
    { header: 'Item', key: 'section', width: 60 },
    { header: 'Code', key: 'code', width: 8 },
    {
      header: `${yearLabel(baseYear, 0)} (Current)`,
      key: 'current',
      width: 14,
      style: { numFmt: ACCOUNTING },
    },
    {
      header: `${yearLabel(baseYear, 1)} (CPI ${defaults.cpiY1}%)`,
      key: 'y1',
      width: 14,
      style: { numFmt: ACCOUNTING },
    },
    {
      header: `${yearLabel(baseYear, 2)} (CPI ${defaults.cpiY2}%)`,
      key: 'y2',
      width: 14,
      style: { numFmt: ACCOUNTING },
    },
    {
      header: `${yearLabel(baseYear, 3)} (CPI ${defaults.cpiY3}%)`,
      key: 'y3',
      width: 14,
      style: { numFmt: ACCOUNTING },
    },
    { header: 'Notes', key: 'notes', width: 36 },
  ];

  // Right-align numbers; left-align text in headers
  ['current', 'y1', 'y2', 'y3'].forEach((key) => {
    ws.getColumn(key).alignment = { horizontal: 'right', vertical: 'middle' };
  });
  ws.getColumn('lineNo').alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getColumn('notes').alignment = { vertical: 'middle', wrapText: true };

  styleRow(ws.getRow(1), 'header');

  let lineNo = 1;

  const writeData = (item: ProjectedLine, indent = '    ') => {
    const r = ws.addRow({
      lineNo: lineNo++,
      section: indent + item.name,
      code: item.code,
      current: round(item.current),
      y1: round(item.y1),
      y2: round(item.y2),
      y3: round(item.y3),
      notes: item.note,
    });
    styleRow(r, 'data');
  };

  const writeSubtotal = (
    label: string,
    totals: { current: number; y1: number; y2: number; y3: number },
  ) => {
    const r = ws.addRow({
      section: `Subtotal — ${label}`,
      current: round(totals.current),
      y1: round(totals.y1),
      y2: round(totals.y2),
      y3: round(totals.y3),
    });
    styleRow(r, 'subtotal');
  };

  const writeTotal = (
    label: string,
    totals: { current: number; y1: number; y2: number; y3: number },
  ) => {
    const r = ws.addRow({
      section: label,
      current: round(totals.current),
      y1: round(totals.y1),
      y2: round(totals.y2),
      y3: round(totals.y3),
    });
    styleRow(r, 'total');
  };

  const writeNet = (
    label: string,
    totals: { current: number; y1: number; y2: number; y3: number },
  ) => {
    const r = ws.addRow({
      section: label,
      current: round(totals.current),
      y1: round(totals.y1),
      y2: round(totals.y2),
      y3: round(totals.y3),
    });
    styleRow(r, 'net');
  };

  const writeBlank = () => ws.addRow({});

  const writeCategoryGroup = (group: CategoryGroup) => {
    const primaryItems = group.category.type === 'income' ? group.incomeItems : group.expenseItems;
    const deductionItems =
      group.category.type === 'income' ? group.expenseItems : group.incomeItems;
    if (primaryItems.length === 0 && deductionItems.length === 0) return;
    const cat = ws.addRow({ section: group.category.name });
    styleRow(cat, 'category');
    for (const item of primaryItems) writeData(item);
    if (deductionItems.length > 0) {
      const label = group.category.type === 'income' ? '   Deductions' : '   Offsets';
      const sh = ws.addRow({ section: label });
      styleRow(sh, 'project-subhead');
      for (const item of deductionItems) writeData(item, '      ');
    }
    writeSubtotal(group.category.name, group.net);
  };

  // ── Operating Income ────────────────────────────────────────────────
  const incomeHeader = ws.addRow({ section: 'OPERATING — INCOME' });
  styleRow(incomeHeader, 'section-header');
  for (const g of derived.incomeByCategory) writeCategoryGroup(g);
  writeTotal('TOTAL OPERATING INCOME', derived.operatingIncomeTotals);
  writeBlank();

  // ── Operating Expenses ──────────────────────────────────────────────
  const expensesHeader = ws.addRow({ section: 'OPERATING — EXPENSES' });
  styleRow(expensesHeader, 'section-header');
  for (const g of derived.expensesByCategory) writeCategoryGroup(g);
  writeTotal('TOTAL OPERATING EXPENSES', derived.operatingExpenseTotals);
  writeNet('Net Operating', derived.operatingNet);
  writeBlank();

  // ── Projects ────────────────────────────────────────────────────────
  if (derived.projects.length > 0) {
    const npHeader = ws.addRow({ section: 'DISCRETE ACTIVITIES' });
    styleRow(npHeader, 'section-header');
    for (const p of derived.projects) {
      const ph = ws.addRow({ section: `Project: ${p.project.name}` });
      styleRow(ph, 'project-header');
      if (p.income.length > 0) {
        const sh = ws.addRow({ section: '   Income' });
        styleRow(sh, 'project-subhead');
        for (const item of p.income) writeData(item, '   ');
      }
      if (p.expenses.length > 0) {
        const sh = ws.addRow({ section: '   Expenses' });
        styleRow(sh, 'project-subhead');
        for (const item of p.expenses) writeData(item, '   ');
      }
      writeNet(`Net — ${p.project.name}`, p.net);
    }
    writeNet('Total Discrete Net', derived.totalProjectsNet);
    writeBlank();
  }

  // ── Grand Total ─────────────────────────────────────────────────────
  const grand = ws.addRow({
    section: 'GRAND TOTAL NET',
    current: round(derived.grandNet.current),
    y1: round(derived.grandNet.y1),
    y2: round(derived.grandNet.y2),
    y3: round(derived.grandNet.y3),
  });
  styleRow(grand, 'grand');

  return ws;
}

export async function buildBudgetXlsx(
  derived: DerivedBudget,
  baseYear: number,
): Promise<ArrayBuffer> {
  const { Workbook: WorkbookImpl } = await import('exceljs');
  const wb = new WorkbookImpl();
  wb.creator = 'CCAANZ Diocesan Budget Tool';
  wb.lastModifiedBy = 'CCAANZ Diocesan Budget Tool';
  wb.created = new Date();
  wb.modified = new Date();
  addBudgetWorksheet(wb, derived, baseYear);
  return await wb.xlsx.writeBuffer();
}

export function xlsxFilename(baseYear: number): string {
  const date = new Date().toISOString().slice(0, 10);
  return `CCAANZ_Budget_${yearLabel(baseYear, 1)}-${yearLabel(baseYear, 3)}_${date}.xlsx`;
}

export async function downloadBudgetXlsx(derived: DerivedBudget, baseYear: number): Promise<void> {
  const buffer = await buildBudgetXlsx(derived, baseYear);
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = xlsxFilename(baseYear);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
